export type VisitorContext = {
  countryIso: string;
  countryName: string;
  scheduleTimeZone: string;
  /** Cómo se inferió el país (zona horaria del navegador, idioma o valor por defecto). */
  countrySource: 'timezone' | 'locale' | 'default';
};

const UNRELIABLE_TIMEZONES = new Set([
  'UTC',
  'Etc/UTC',
  'GMT',
  'Etc/GMT',
  'Etc/GMT+0',
  'Etc/GMT-0',
]);

export const TIMEZONE_TO_COUNTRY_ISO: Record<string, string> = {
  'America/Montevideo': 'UY',
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Santiago': 'CL',
  'America/Asuncion': 'PY',
  'America/Sao_Paulo': 'BR',
  'America/La_Paz': 'BO',
  'America/Lima': 'PE',
  'America/Bogota': 'CO',
  'America/Guayaquil': 'EC',
  'America/Caracas': 'VE',
  'America/Mexico_City': 'MX',
  'America/Costa_Rica': 'CR',
  'America/Panama': 'PA',
  'America/Guatemala': 'GT',
  'America/Santo_Domingo': 'DO',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'Europe/Madrid': 'ES',
  'Europe/Lisbon': 'PT',
  'Europe/London': 'GB',
  'Europe/Berlin': 'DE',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
};

export const COUNTRY_PRIMARY_TIMEZONE: Record<string, string> = {
  UY: 'America/Montevideo',
  AR: 'America/Argentina/Buenos_Aires',
  CL: 'America/Santiago',
  PY: 'America/Asuncion',
  BR: 'America/Sao_Paulo',
  BO: 'America/La_Paz',
  PE: 'America/Lima',
  CO: 'America/Bogota',
  EC: 'America/Guayaquil',
  VE: 'America/Caracas',
  MX: 'America/Mexico_City',
  CR: 'America/Costa_Rica',
  PA: 'America/Panama',
  GT: 'America/Guatemala',
  DO: 'America/Santo_Domingo',
  US: 'America/New_York',
  CA: 'America/Toronto',
  ES: 'Europe/Madrid',
  PT: 'Europe/Lisbon',
  GB: 'Europe/London',
  DE: 'Europe/Berlin',
  FR: 'Europe/Paris',
  IT: 'Europe/Rome',
};

const SUPPORTED_COUNTRY_ISOS = new Set(Object.keys(COUNTRY_PRIMARY_TIMEZONE));

function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function getLocaleRegionIso(): string | null {
  try {
    const langs =
      typeof navigator !== 'undefined'
        ? [...(navigator.languages ?? []), navigator.language].filter(Boolean)
        : [];
    for (const loc of langs) {
      const region = /-([A-Z]{2})\b/i.exec(loc)?.[1]?.toUpperCase();
      if (region && SUPPORTED_COUNTRY_ISOS.has(region)) return region;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getCountryDisplayName(countryIso: string, locale = 'es'): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: 'region' });
    return dn.of(countryIso) ?? countryIso;
  } catch {
    return countryIso;
  }
}

export function guessVisitorCountryIso(): string {
  const tz = getBrowserTimeZone();
  if (tz && TIMEZONE_TO_COUNTRY_ISO[tz]) return TIMEZONE_TO_COUNTRY_ISO[tz]!;

  const fromLocale = getLocaleRegionIso();
  if (fromLocale) return fromLocale;

  return 'UY';
}

export function resolveScheduleTimeZone(countryIso = guessVisitorCountryIso()): string {
  const browserTz = getBrowserTimeZone();

  if (browserTz && !UNRELIABLE_TIMEZONES.has(browserTz)) {
    return browserTz;
  }

  return COUNTRY_PRIMARY_TIMEZONE[countryIso] ?? 'America/Montevideo';
}

export function resolveVisitorContext(): VisitorContext {
  const browserTz = getBrowserTimeZone();
  const countryFromTz = browserTz ? TIMEZONE_TO_COUNTRY_ISO[browserTz] : undefined;
  const countryFromLocale = getLocaleRegionIso();

  let countryIso: string;
  let countrySource: VisitorContext['countrySource'];
  if (countryFromTz) {
    countryIso = countryFromTz;
    countrySource = 'timezone';
  } else if (countryFromLocale) {
    countryIso = countryFromLocale;
    countrySource = 'locale';
  } else {
    countryIso = 'UY';
    countrySource = 'default';
  }

  return {
    countryIso,
    countryName: getCountryDisplayName(countryIso),
    scheduleTimeZone: resolveScheduleTimeZone(countryIso),
    countrySource,
  };
}

export function isValidNationalPhone(national: string, minDigits = 6): boolean {
  return national.replace(/\D/g, '').length >= minDigits;
}

export function isValidFullPhone(phone: string): boolean {
  const trimmed = phone.trim();
  if (!trimmed.startsWith('+')) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}
